import React from 'react';
import {
  Link,
  NavLink,
  useNavigate,
  useParams,
  type LinkProps,
  type NavLinkProps,
  type NavigateOptions,
} from 'react-router-dom';
import { DEFAULT_LANG, isLanguageCode } from './languages';

// The active language from the /:lang route param, with a safe fallback.
export function useLang(): string {
  const { lang } = useParams<{ lang: string }>();
  return isLanguageCode(lang) ? lang : DEFAULT_LANG;
}

// Prefix an app-absolute path ("/locations") with the active language
// ("/en/locations"). External URLs and already-prefixed paths pass through.
function withLang(path: string, lang: string): string {
  if (/^[a-z]+:|^\/\//i.test(path) || path.startsWith('#')) return path;
  if (!path.startsWith('/')) return path; // relative — leave as-is
  const seg = path.split('/')[1];
  if (isLanguageCode(seg)) return path; // already localized
  return path === '/' ? `/${lang}` : `/${lang}${path}`;
}

// Drop-in replacement for useNavigate that auto-prefixes the language.
export function useLocalizedNavigate() {
  const navigate = useNavigate();
  const lang = useLang();
  return React.useCallback(
    (to: string | number, options?: NavigateOptions) => {
      if (typeof to === 'number') return navigate(to);
      return navigate(withLang(to, lang), options);
    },
    [navigate, lang],
  );
}

// Drop-in <Link> that prefixes string `to` with the active language.
export const LocalLink: React.FC<LinkProps> = ({ to, ...rest }) => {
  const lang = useLang();
  const resolved = typeof to === 'string' ? withLang(to, lang) : to;
  return <Link to={resolved} {...rest} />;
};

// Drop-in <NavLink> that prefixes string `to` with the active language.
export const LocalNavLink: React.FC<NavLinkProps> = ({ to, ...rest }) => {
  const lang = useLang();
  const resolved = typeof to === 'string' ? withLang(to, lang) : to;
  return <NavLink to={resolved} {...rest} />;
};
